import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  useStreamStatus,
  StreamStatus as BackendStreamStatus,
} from "../../../../context/StreamStatusContext";
import { useWsProvider } from "../../../../context/NativeApplicationContext";
import { toast } from "react-toastify";

interface StreamData {
  streamName: string;
  description: string;
  thumbnail: any;
  platforms: {
    twitch: boolean;
    youtube: boolean;
  };
}

interface GoLiveContextProps {
  step: 1 | 2;
  setStep: (step: 1 | 2) => void;
  streamData: StreamData;
  setStreamData: React.Dispatch<React.SetStateAction<StreamData>>;
  isLive: boolean;
  isLoading: boolean;
  error: string | null;
  streamDuration: number;
  // Actions
  createAndStartStream: () => Promise<boolean>;
  stopCurrentStream: () => Promise<boolean>;
  clearError: () => void;
}

const GoLiveContext = createContext<GoLiveContextProps | undefined>(undefined);

export const GoLiveProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [streamData, setStreamData] = useState<StreamData>({
    streamName: "",
    description: "",
    thumbnail: null,
    platforms: {
      twitch: false,
      youtube: false,
    },
  });
  const [streamDuration, setStreamDuration] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);

  // Get contexts
  const {
    activeStream,
    streamerStatus,
    createStream,
    endStream,
    isLoading: backendLoading,
    error: backendError,
    clearError: clearBackendError,
    notifyNativeStreamingChange,
    accessToken,
    refreshStatus,
  } = useStreamStatus();

  const {
    startStreaming,
    stopStreaming,
    streamStarted,
    isNativeAppConnected,
    canStartStreaming,
    canStopStreaming,
    setOnStreamingStatusChange,
  } = useWsProvider();

  // Register callback to notify StreamStatusContext of native app changes
  useEffect(() => {
    setOnStreamingStatusChange(notifyNativeStreamingChange);
    return () => setOnStreamingStatusChange(null);
  }, [setOnStreamingStatusChange, notifyNativeStreamingChange]);

  // Trigger status refresh when access token becomes available
  useEffect(() => {
    if (accessToken) {
      refreshStatus();
    }
  }, [accessToken, refreshStatus]);

  // Auto-navigate to step 2 if user is already streaming (reactive to state changes)
  useEffect(() => {
    if (streamerStatus === "streaming" && activeStream) {
      // Pre-fill form data from active stream
      setStreamData((prev) => ({
        ...prev,
        streamName: activeStream.metadata?.title || "",
        description: activeStream.metadata?.description || "",
        platforms: {
          youtube: !!activeStream.platforms.youtube,
          twitch: !!activeStream.platforms.twitch,
        },
      }));
      setStep(2);
    }
  }, [streamerStatus, activeStream]);

  // Determine if live based on backend status
  const isLive = streamerStatus === "streaming" && streamStarted;

  // Duration timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLive) {
      interval = setInterval(() => {
        setStreamDuration((prev) => prev + 1);
      }, 1000);
    } else {
      setStreamDuration(0);
    }
    return () => clearInterval(interval);
  }, [isLive]);

  // Combined loading and error states
  const isLoading = backendLoading;
  const error = localError || backendError;

  const clearError = useCallback(() => {
    setLocalError(null);
    clearBackendError();
  }, [clearBackendError]);

  // Start broadcasting (stream should already be created in Step 1)
  const createAndStartStream = useCallback(async (): Promise<boolean> => {
    clearError();

    if (!isNativeAppConnected) {
      setLocalError("Please connect to KO Broadcaster first.");
      toast.error("Please connect to KO Broadcaster first.");
      return false;
    }

    if (!canStartStreaming) {
      setLocalError("Cannot start streaming in current state.");
      toast.error("Cannot start streaming in current state.");
      return false;
    }

    try {
      // Use existing active stream (created in Step 1) or create new one if needed
      let stream = activeStream;

      if (!stream) {
        // Fallback: create stream if not already created (shouldn't normally happen)
        if (!streamData.platforms.youtube && !streamData.platforms.twitch) {
          setLocalError("Please select at least one platform.");
          toast.error("Please select at least one platform.");
          return false;
        }

        stream = await createStream({
          title: streamData.streamName || "Live Stream",
          description: streamData.description || "Live from Squadifi",
          platforms: {
            youtube: streamData.platforms.youtube,
            twitch: streamData.platforms.twitch,
          },
        });

        if (!stream) {
          toast.error("Failed to create stream on backend.");
          return false;
        }
      }

      // Extract stream keys from the stream (either existing or newly created)
      const youtubeKey = stream.platforms?.youtube?.streamKey || "";
      const twitchKey = stream.platforms?.twitch?.streamKey || "";

      console.log("Stream keys from active stream:", {
        youtube: !!youtubeKey,
        twitch: !!twitchKey,
      });

      // Start native broadcaster
      await startStreaming(youtubeKey || undefined, twitchKey || undefined);

      toast.success("Stream started successfully!");
      return true;
    } catch (err: any) {
      console.error("Failed to start stream:", err);
      setLocalError(err.message || "Failed to start stream");
      toast.error(`Failed to start stream: ${err.message}`);
      return false;
    }
  }, [
    clearError,
    isNativeAppConnected,
    canStartStreaming,
    activeStream,
    streamData,
    createStream,
    startStreaming,
  ]);

  // Stop current stream
  const stopCurrentStream = useCallback(async (): Promise<boolean> => {
    clearError();

    if (!canStopStreaming) {
      setLocalError("Cannot stop streaming - not currently streaming.");
      return false;
    }

    try {
      // 1. End stream on backend first (mark as completed)
      const success = await endStream();
      if (!success) {
        toast.error("Failed to end stream on backend.");
        return false;
      }

      // 2. Stop native broadcaster
      stopStreaming();

      toast.success("Stream ended successfully.");
      return true;
    } catch (err: any) {
      console.error("Failed to stop stream:", err);
      setLocalError(err.message || "Failed to stop stream");
      toast.error(`Failed to stop stream: ${err.message}`);
      return false;
    }
  }, [clearError, canStopStreaming, endStream, stopStreaming]);

  return (
    <GoLiveContext.Provider
      value={{
        step,
        setStep,
        streamData,
        setStreamData,
        isLive,
        isLoading,
        error,
        streamDuration,
        createAndStartStream,
        stopCurrentStream,
        clearError,
      }}
    >
      {children}
    </GoLiveContext.Provider>
  );
};

export const useGoLive = () => {
  const context = useContext(GoLiveContext);
  if (!context) {
    throw new Error("useGoLive must be used within a GoLiveProvider");
  }
  return context;
};
